import { useState } from 'react';

import type {
  CreditParameterCatalog,
  CreditParameterSet,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HistoryTab } from '@/shared/ui/HistoryTab';
import { Modal } from '@/shared/ui/Modal';

import { ComparacaoDeVersoes } from './ComparacaoDeVersoes';
import type { EscopoDoEditor } from './escopo';
import {
  rotuloDaOrigem,
  rotuloDaRevogacao,
  rotuloDaSituacao,
  situacaoDe,
  textoDasAprovacoes,
  type SituacaoDaVersao,
} from './valores';

const tomDaSituacao: Record<
  SituacaoDaVersao,
  'success' | 'warning' | 'neutral' | 'info'
> = {
  ativa: 'success',
  agendada: 'warning',
  rascunho: 'info',
  revogada: 'neutral',
};

export function BadgeDaSituacao({ versao }: { versao: CreditParameterSet }) {
  const situacao = situacaoDe(versao);
  return <Badge tone={tomDaSituacao[situacao]}>{rotuloDaSituacao[situacao]}</Badge>;
}

const acoesDaTrilha: Record<string, string> = {
  'credito.parametro.carga_inicial': 'Carga inicial',
  'credito.parametro.create': 'Rascunho aberto',
  'credito.parametro.update': 'Rascunho editado',
  'credito.parametro.approve': 'Versao ativada',
  'credito.parametro.approve_partial': 'Aprovacao registrada (falta outra pessoa)',
  'credito.parametro.schedule': 'Versao agendada',
  'credito.parametro.schedule_cancel': 'Agendamento cancelado',
  'credito.parametro.effective': 'Versao agendada entrou em vigor',
  'credito.parametro.revoke': 'Versao revogada',
};

const camposDaTrilha: Record<string, string> = {
  version: 'Versao',
  status: 'Situacao',
  values_hash: 'Resumo dos valores',
  source: 'Origem',
  homologated: 'Regras homologadas',
  changed: 'Parametros alterados',
  justification: 'Justificativa',
  replaced_by_version: 'Substituida pela versao',
  effective_from: 'Inicio da vigencia',
  effective_until: 'Fim da vigencia',
  revocation_kind: 'Motivo da revogacao',
  based_on_global_version: 'Versao global de origem',
  approvals: 'Aprovacoes registradas',
  required_approvals: 'Aprovacoes exigidas',
  requested_effective_from: 'Vigencia pedida',
  responsible_label: 'Responsavel',
  responsible_id: 'Responsavel (identificador)',
  approved_at: 'Aprovada em',
  recorded_at: 'Gravada em',
  parameter_set_id: 'Versao (identificador)',
};

/** Inicio e fim da vigencia. A agendada cancelada nunca valeu. */
function textoDaVigencia(versao: CreditParameterSet): string {
  if (!versao.effective_from) return '-';
  if (versao.revocation_kind === 'agendamento_cancelado') {
    return `Nao entrou em vigor (agendada para ${formatDateTime(versao.effective_from)})`;
  }
  if (versao.effective_until) {
    return `${formatDateTime(versao.effective_from)} ate ${formatDateTime(versao.effective_until)}`;
  }
  return `A partir de ${formatDateTime(versao.effective_from)}`;
}

function quemEQuando(rotulo: string, instante: string | null | undefined) {
  if (!instante) return <span className="text-ink-400">-</span>;
  return (
    <div className="flex flex-col">
      <span>{rotulo || 'sistema'}</span>
      <span className="text-2xs text-ink-500">{formatDateTime(instante)}</span>
    </div>
  );
}

/**
 * Historico das versoes: quem criou, editou, ativou e revogou cada uma, quando, com
 * que justificativa e em que situacao esta. Duas versoes quaisquer se comparam
 * parametro a parametro, e a trilha de cada uma mostra cada passo.
 */
export function PainelDeVersoes({
  escopo,
  versoes,
  catalogo,
  rotulos,
}: {
  escopo: EscopoDoEditor;
  versoes: CreditParameterSet[];
  catalogo: CreditParameterCatalog;
  rotulos: Record<string, string>;
}) {
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [comparando, setComparando] = useState<[string, string] | null>(null);
  const [trilha, setTrilha] = useState<CreditParameterSet | null>(null);

  function alternar(id: string, marcada: boolean) {
    setComparando(null);
    setSelecionadas((atuais) => {
      if (!marcada) return atuais.filter((s) => s !== id);
      // Duas por vez: a terceira marcada tira a mais antiga da selecao.
      return [...atuais.filter((s) => s !== id), id].slice(-2);
    });
  }

  const porId = new Map(versoes.map((v) => [v.id, v]));
  const escolhidas = selecionadas
    .map((id) => porId.get(id))
    .filter((v): v is CreditParameterSet => v !== undefined)
    .sort((a, b) => b.version - a.version);

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title="Historico de versoes"
          description="Da mais nova para a mais antiga. Marque duas versoes para comparar."
          action={
            escolhidas.length === 2 ? (
              <Button
                size="sm"
                onClick={() =>
                  setComparando([escolhidas[0]?.id ?? '', escolhidas[1]?.id ?? ''])
                }
              >
                {`Comparar a versao ${escolhidas[0]?.version} com a versao ${escolhidas[1]?.version}`}
              </Button>
            ) : undefined
          }
        />
        <CardBody className="p-0">
          <DataTable<CreditParameterSet>
            columns={[
              {
                key: 'comparar',
                header: 'Comparar',
                render: (versao) => (
                  <input
                    type="checkbox"
                    aria-label={`Comparar a versao ${versao.version}`}
                    checked={selecionadas.includes(versao.id)}
                    onChange={(event) => alternar(versao.id, event.target.checked)}
                  />
                ),
              },
              {
                key: 'versao',
                header: 'Versao',
                render: (versao) => (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink-900">{versao.version}</span>
                    <BadgeDaSituacao versao={versao} />
                  </div>
                ),
              },
              {
                key: 'origem',
                header: 'Origem',
                render: (versao) => rotuloDaOrigem(versao),
              },
              {
                key: 'criada',
                header: 'Criada',
                render: (versao) =>
                  quemEQuando(versao.created_label, versao.created_at),
              },
              {
                key: 'editada',
                header: 'Ultima edicao',
                render: (versao) =>
                  versao.updated_label
                    ? quemEQuando(versao.updated_label, versao.updated_at)
                    : quemEQuando('', null),
              },
              {
                key: 'ativada',
                header: 'Ativada',
                render: (versao) =>
                  quemEQuando(versao.activated_label, versao.activated_at),
              },
              {
                key: 'vigencia',
                header: 'Vigencia',
                render: (versao) => textoDaVigencia(versao),
              },
              {
                key: 'justificativa',
                header: 'Justificativa',
                render: (versao) =>
                  versao.status === 'rascunho' && versao.approvals.length > 0
                    ? textoDasAprovacoes(versao.approvals.length)
                    : versao.activation_note || '-',
              },
              {
                key: 'revogada',
                header: 'Revogada',
                render: (versao) => (
                  <div className="flex flex-col gap-0.5">
                    {quemEQuando(versao.revoked_label, versao.revoked_at)}
                    {rotuloDaRevogacao(versao) ? (
                      <span className="text-2xs text-ink-600">
                        {rotuloDaRevogacao(versao)}
                      </span>
                    ) : null}
                    {versao.revocation_note ? (
                      <span className="text-2xs text-ink-500">
                        {versao.revocation_note}
                      </span>
                    ) : null}
                  </div>
                ),
              },
              ...(escopo.tipo === 'empresa'
                ? [
                    {
                      key: 'trilha',
                      header: 'Trilha',
                      render: (versao: CreditParameterSet) => (
                        <AcaoComIcone
                          acao="historico"
                          rotulo="Trilha"
                          aria-label={`Trilha da versao ${versao.version}`}
                          onClick={() => setTrilha(versao)}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            items={versoes}
            keyOf={(versao) => versao.id}
            empty={<EmptyState icon="layers" title="Nenhuma versao de parametros" />}
          />
        </CardBody>
      </Card>

      {comparando ? (
        <Card>
          <CardHeader title="Comparacao entre versoes" />
          <CardBody>
            <ComparacaoDeVersoes
              escopo={escopo}
              catalogo={catalogo}
              rotulos={rotulos}
              baseId={comparando[0]}
              outraId={comparando[1]}
            />
          </CardBody>
        </Card>
      ) : null}

      <Modal
        open={trilha !== null}
        onClose={() => setTrilha(null)}
        size="lg"
        title={trilha ? `Trilha da versao ${trilha.version}` : 'Trilha'}
      >
        {trilha ? (
          <HistoryTab
            entityType="credit_parameter_set"
            entityId={trilha.id}
            title="Passos da versao"
            description="Quem criou, editou, ativou e revogou, quando, e o que mudou em cada passo."
            emptyText="Nenhum passo registrado"
            actionLabel={(acao) => acoesDaTrilha[acao] ?? acao}
            fieldLabel={(campo) => camposDaTrilha[campo] ?? campo}
          />
        ) : null}
      </Modal>
    </div>
  );
}
